export interface SiteTreeNode {
  id: number;
  name: string;
  parentSiteId: number | null;
  timeZone: string | null;
  locatedInEu: boolean | null;
  descendantIds: number[];
  children: SiteTreeNode[];
}

export interface FlatSiteTreeNode {
  node: SiteTreeNode;
  depth: number;
  path: string[];
}

export function flattenSiteTree(tree: SiteTreeNode[]): FlatSiteTreeNode[] {
  const rows: FlatSiteTreeNode[] = [];

  const walk = (node: SiteTreeNode, depth: number, path: string[]) => {
    const nextPath = [...path, node.name];
    rows.push({ node, depth, path: nextPath });
    for (const child of node.children) {
      walk(child, depth + 1, nextPath);
    }
  };

  for (const root of tree) {
    walk(root, 0, []);
  }

  return rows;
}

export function buildVisibleSiteRows(
  tree: SiteTreeNode[],
  siteIds: number[]
): FlatSiteTreeNode[] {
  const siteIdSet = new Set(siteIds);
  const rows = flattenSiteTree(tree).filter((row) => siteIdSet.has(row.node.id));
  const seen = new Set(rows.map((row) => row.node.id));

  const fallbackRows = siteIds
    .filter((siteId) => !seen.has(siteId))
    .map((siteId) => ({
      node: {
        id: siteId,
        name: `Site ${siteId}`,
        parentSiteId: null,
        timeZone: null,
        locatedInEu: null,
        descendantIds: [],
        children: [],
      },
      depth: 0,
      path: [`Site ${siteId}`],
    }));

  return [...rows, ...fallbackRows];
}

export function siteLabel(
  siteId: number | null | undefined,
  namesById: Map<number, string>
): string {
  if (siteId == null) return 'Unknown site';
  const name = namesById.get(siteId);
  return name ? `${name} (#${siteId})` : `Site ${siteId}`;
}

export function siteOptionLabel(row: FlatSiteTreeNode): string {
  const prefix = row.depth > 0 ? `${'-- '.repeat(row.depth)}` : '';
  return `${prefix}${row.node.name} (#${row.node.id})`;
}

export function buildSiteNameMap(tree: SiteTreeNode[]): Map<number, string> {
  const names = new Map<number, string>();
  for (const row of flattenSiteTree(tree)) {
    names.set(row.node.id, row.node.name);
  }
  return names;
}

/**
 * Returns every site id present in the loaded tree (root + descendants).
 * Used as a fallback when an account has no explicit site_ids configured —
 * the tree loaded from Nova for that account is the authoritative access set.
 */
export function collectSiteIdsFromTree(tree: SiteTreeNode[]): number[] {
  const ids = new Set<number>();
  for (const row of flattenSiteTree(tree)) {
    ids.add(row.node.id);
  }
  return Array.from(ids).sort((left, right) => left - right);
}
